import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Grid,
  useTheme,
  useMediaQuery,
  Fab,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  MoreVert,
  Schedule,
  ContentCopy,
  AutoMode,
  Today
} from '@mui/icons-material';
import { API_URL } from '../../utils/apiConfig';
import { AuthContext } from '../../context/AuthContext';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' }
];

const PREFERENCE_COLORS = {
  'preferred': { bg: '#e8f5e8', border: '#4caf50', text: '#2e7d32' },
  'available': { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' },
  'unavailable': { bg: '#ffebee', border: '#f44336', text: '#c62828' }
};

const TEMPLATES = [
  {
    id: 'weekday_morning',
    name: 'Weekday Mornings',
    description: 'Available 9 AM - 1 PM, Monday through Friday',
    pattern: [
      { day: 1, start: '09:00', end: '13:00', preference: 'available' },
      { day: 2, start: '09:00', end: '13:00', preference: 'available' },
      { day: 3, start: '09:00', end: '13:00', preference: 'available' },
      { day: 4, start: '09:00', end: '13:00', preference: 'available' },
      { day: 5, start: '09:00', end: '13:00', preference: 'available' }
    ]
  },
  {
    id: 'weekend_shifts',
    name: 'Weekend Shifts',
    description: 'Available weekends 10 AM - 8 PM',
    pattern: [
      { day: 6, start: '10:00', end: '20:00', preference: 'available' },
      { day: 0, start: '10:00', end: '20:00', preference: 'available' }
    ]
  },
  {
    id: 'full_time',
    name: 'Full Time Schedule',
    description: 'Available 9 AM - 5 PM, Monday through Friday',
    pattern: [
      { day: 1, start: '09:00', end: '17:00', preference: 'preferred' },
      { day: 2, start: '09:00', end: '17:00', preference: 'preferred' },
      { day: 3, start: '09:00', end: '17:00', preference: 'preferred' },
      { day: 4, start: '09:00', end: '17:00', preference: 'preferred' },
      { day: 5, start: '09:00', end: '17:00', preference: 'preferred' }
    ]
  },
  {
    id: 'evening_shifts',
    name: 'Evening Shifts',
    description: 'Available 5 PM - 11 PM all week',
    pattern: DAYS_OF_WEEK.map(day => ({
      day: day.value,
      start: '17:00',
      end: '23:00',
      preference: 'available'
    }))
  }
];

// Enhanced availability block with better time display
function AvailabilityBlock({ availability, onEdit, onDelete }) {
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const colors = PREFERENCE_COLORS[availability.preference];
  
  return (
    <Card
      sx={{
        mb: 1,
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: 2,
        cursor: 'pointer',
        '&:hover': { 
          transform: 'translateY(-1px)',
          boxShadow: 2
        }
      }}
      onClick={() => onEdit(availability)}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1}>
          {/* Time range - improved formatting */}
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600, 
              color: colors.text,
              fontSize: '0.85rem',
              lineHeight: 1.2,
              wordBreak: 'keep-all',
              whiteSpace: 'nowrap'
            }}
          >
            {formatTime(availability.start_time)} - {formatTime(availability.end_time)}
          </Typography>
          
          {/* Preference chip and actions */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Chip
              label={availability.preference}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: colors.border,
                color: 'white',
                fontWeight: 600,
                textTransform: 'capitalize'
              }}
            />
            <Stack direction="row" spacing={0.5}>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(availability);
                }}
                sx={{ 
                  color: colors.text,
                  width: 24,
                  height: 24
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(availability.id);
                }}
                sx={{ 
                  color: 'error.main',
                  width: 24,
                  height: 24
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Enhanced availability form dialog
function AvailabilityDialog({ open, availability, onSave, onClose, onDelete, preselectedDay }) {
  const [formData, setFormData] = useState({
    day_of_week: preselectedDay || 1,
    start_time: '09:00',
    end_time: '17:00',
    preference: 'available'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (availability) {
      setFormData({
        day_of_week: availability.day_of_week,
        start_time: availability.start_time,
        end_time: availability.end_time,
        preference: availability.preference
      });
    } else {
      setFormData({
        day_of_week: preselectedDay || 1,
        start_time: '09:00',
        end_time: '17:00',
        preference: 'available'
      });
    }
    setErrors({});
  }, [availability, open, preselectedDay]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.start_time) newErrors.start_time = 'Start time is required';
    if (!formData.end_time) newErrors.end_time = 'End time is required';
    
    if (formData.start_time && formData.end_time) {
      const startMinutes = timeToMinutes(formData.start_time);
      const endMinutes = timeToMinutes(formData.end_time);
      if (endMinutes <= startMinutes) {
        newErrors.end_time = 'End time must be after start time';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {availability ? 'Edit Availability' : 'Add Availability'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={formData.day_of_week}
                onChange={(e) => handleChange('day_of_week', e.target.value)}
                label="Day of Week"
              >
                {DAYS_OF_WEEK.map(day => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              type="time"
              label="Start Time"
              value={formData.start_time}
              onChange={(e) => handleChange('start_time', e.target.value)}
              error={!!errors.start_time}
              helperText={errors.start_time}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              type="time"
              label="End Time"
              value={formData.end_time}
              onChange={(e) => handleChange('end_time', e.target.value)}
              error={!!errors.end_time}
              helperText={errors.end_time}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Preference</InputLabel>
              <Select
                value={formData.preference}
                onChange={(e) => handleChange('preference', e.target.value)}
                label="Preference"
              >
                <MenuItem value="preferred">Preferred</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {availability && (
          <Button
            onClick={() => onDelete(availability.id)}
            color="error"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" startIcon={<Save />}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Template selection dialog
function TemplateDialog({ open, onClose, onApplyTemplate }) {
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const handleApply = () => {
    if (selectedTemplate) {
      const template = TEMPLATES.find(t => t.id === selectedTemplate);
      onApplyTemplate(template);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply Availability Template</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a pre-defined template to quickly set up availability patterns. This will replace existing availability.
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Template</InputLabel>
          <Select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            label="Template"
          >
            {TEMPLATES.map(template => (
              <MenuItem key={template.id} value={template.id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {template.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {template.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedTemplate && (
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
            {TEMPLATES.find(t => t.id === selectedTemplate)?.pattern.map((item, idx) => {
              const day = DAYS_OF_WEEK.find(d => d.value === item.day);
              return (
                <Typography key={idx} variant="caption" display="block">
                  {day?.label}: {item.start} - {item.end} ({item.preference})
                </Typography>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleApply} 
          variant="contained" 
          disabled={!selectedTemplate}
        >
          Apply Template
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AvailabilityCalendarEditor({ employeeId, isManager = false, selectedLocation = 'all' }) {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [preselectedDay, setPreselectedDay] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    if (isManager) {
      fetchEmployees();
    } else if (employeeId) {
      setSelectedEmployee({ id: employeeId });
      fetchAvailability(employeeId);
    }
  }, [employeeId, isManager, selectedLocation]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      const locationId = selectedLocation === 'all' ? (user?.store || null) : selectedLocation;
      if (locationId) params.set('locationId', locationId);
      params.set('limit', '100');

      const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/employees?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok) {
        setEmployees(data.data || []);
        if (data.data && data.data.length > 0) {
          setSelectedEmployee(data.data[0]);
          fetchAvailability(data.data[0].id);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (empId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/availability?employeeId=${empId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok) {
        setAvailability(data.data || []);
      } else {
        setError(data.error || 'Failed to fetch availability');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAvailability = async (availData) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingAvailability
        ? `${API_URL.replace(/\/$/, '')}/vnext/availability/${editingAvailability.id}`
        : `${API_URL.replace(/\/$/, '')}/vnext/availability`;
      const method = editingAvailability ? 'PATCH' : 'POST';

      const payload = {
        ...availData,
        employee_id: selectedEmployee.id
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save availability');
      }

      setDialogOpen(false);
      setEditingAvailability(null);
      setPreselectedDay(null);
      fetchAvailability(selectedEmployee.id);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeleteAvailability = async (availId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/availability/${availId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete availability');
      }

      setDialogOpen(false);
      setEditingAvailability(null);
      fetchAvailability(selectedEmployee.id);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleApplyTemplate = async (template) => {
    try {
      const token = localStorage.getItem('token');
      
      // Clear existing availability
      await fetch(`${API_URL.replace(/\/$/, '')}/vnext/availability?employeeId=${selectedEmployee.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      // Apply template
      const patterns = template.pattern.map(p => ({
        employee_id: selectedEmployee.id,
        day_of_week: p.day,
        start_time: p.start,
        end_time: p.end,
        preference: p.preference
      }));

      const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/availability/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ availability: patterns })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to apply template');
      }

      fetchAvailability(selectedEmployee.id);
    } catch (e) {
      setError(e.message);
    }
  };

  const generateDefaultAvailability = async () => {
    if (!window.confirm('Generate standard 9-5 availability for all weekdays? This will replace existing availability.')) return;

    try {
      const token = localStorage.getItem('token');

      // Delete existing availability
      await fetch(`${API_URL.replace(/\/$/, '')}/vnext/availability?employeeId=${selectedEmployee.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create weekday availability (Monday-Friday)
      const defaultAvail = [];
      for (let day = 1; day <= 5; day++) {
        defaultAvail.push({
          employee_id: selectedEmployee.id,
          day_of_week: day,
          start_time: '09:00',
          end_time: '17:00',
          preference: 'available'
        });
      }

      const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/availability/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ availability: defaultAvail })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate default availability');
      }

      fetchAvailability(selectedEmployee.id);
    } catch (e) {
      setError(e.message);
    }
  };

  // Group availability by day of week
  const availabilityByDay = availability.reduce((acc, avail) => {
    if (!acc[avail.day_of_week]) acc[avail.day_of_week] = [];
    acc[avail.day_of_week].push(avail);
    return acc;
  }, {});

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Today color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Weekly Availability Editor
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Desktop Actions */}
        {!isMobile && selectedEmployee && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => setError('Copy previous week feature coming soon!')}
              size="small"
            >
              Copy Previous Week
            </Button>
            <Button
              variant="outlined"
              startIcon={<Schedule />}
              onClick={() => setTemplateDialogOpen(true)}
              size="small"
            >
              Apply Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoMode />}
              onClick={generateDefaultAvailability}
              size="small"
            >
              Auto-Generate
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setEditingAvailability(null);
                setPreselectedDay(null);
                setDialogOpen(true);
              }}
              size="small"
            >
              Add Availability
            </Button>
          </Stack>
        )}

        {/* Mobile Menu */}
        {isMobile && selectedEmployee && (
          <>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem onClick={() => { setTemplateDialogOpen(true); setMenuAnchor(null); }}>
                <ListItemIcon><Schedule /></ListItemIcon>
                <ListItemText>Apply Template</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { generateDefaultAvailability(); setMenuAnchor(null); }}>
                <ListItemIcon><AutoMode /></ListItemIcon>
                <ListItemText>Auto-Generate</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Employee Selector for Managers */}
      {isManager && (
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select Employee</InputLabel>
            <Select
              value={selectedEmployee?.id || ''}
              onChange={(e) => {
                const emp = employees.find(emp => emp.id === e.target.value);
                setSelectedEmployee(emp);
                fetchAvailability(emp.id);
              }}
              label="Select Employee"
            >
              {employees.map(emp => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} - {emp.role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {selectedEmployee && (
        <>
          {/* Employee Info */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6">
                {selectedEmployee.first_name} {selectedEmployee.last_name}
              </Typography>
              <Chip label={selectedEmployee.role} color="primary" size="small" />
            </Stack>
          </Box>

          {/* 7-Day Calendar Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(7, 1fr)',
            gap: 2,
            mb: 3
          }}>
            {DAYS_OF_WEEK.map((day) => {
              const dayAvailability = availabilityByDay[day.value] || [];
              const isToday = new Date().getDay() === day.value;
              
              return (
                <Card 
                  key={day.value} 
                  variant="outlined" 
                  sx={{ 
                    minHeight: isMobile ? 120 : 300,
                    border: isToday ? '2px solid' : '1px solid',
                    borderColor: isToday ? 'primary.main' : 'divider'
                  }}
                >
                  <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Day Header */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: isToday ? 'primary.main' : 'text.primary'
                        }}
                      >
                        {isMobile ? day.short : day.label}
                      </Typography>
                      {isToday && (
                        <Typography variant="caption" color="primary">
                          Today
                        </Typography>
                      )}
                    </Box>

                    <Divider sx={{ mb: 1 }} />

                    {/* Availability Blocks */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                      {dayAvailability.length > 0 ? (
                        dayAvailability.map(avail => (
                          <AvailabilityBlock
                            key={avail.id}
                            availability={avail}
                            onEdit={(avail) => {
                              setEditingAvailability(avail);
                              setDialogOpen(true);
                            }}
                            onDelete={handleDeleteAvailability}
                          />
                        ))
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': { 
                              bgcolor: 'action.hover',
                              borderRadius: 1
                            }
                          }}
                          onClick={() => {
                            setEditingAvailability(null);
                            setPreselectedDay(day.value);
                            setDialogOpen(true);
                          }}
                        >
                          <Stack alignItems="center" spacing={1}>
                            <Add sx={{ fontSize: 32, opacity: 0.5 }} />
                            <Typography variant="body2">
                              Add availability
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Box>

                    {/* Quick add button for mobile */}
                    {isMobile && dayAvailability.length > 0 && (
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => {
                          setEditingAvailability(null);
                          setPreselectedDay(day.value);
                          setDialogOpen(true);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Add
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* Legend */}
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            {Object.entries(PREFERENCE_COLORS).map(([key, colors]) => (
              <Chip
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                sx={{
                  backgroundColor: colors.bg,
                  color: colors.text,
                  border: `1px solid ${colors.border}`
                }}
              />
            ))}
          </Stack>
        </>
      )}

      {/* Mobile FAB */}
      {isMobile && selectedEmployee && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => {
            setEditingAvailability(null);
            setPreselectedDay(null);
            setDialogOpen(true);
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Dialogs */}
      <AvailabilityDialog
        open={dialogOpen}
        availability={editingAvailability}
        preselectedDay={preselectedDay}
        onSave={handleSaveAvailability}
        onClose={() => {
          setDialogOpen(false);
          setEditingAvailability(null);
          setPreselectedDay(null);
        }}
        onDelete={handleDeleteAvailability}
      />

      <TemplateDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onApplyTemplate={handleApplyTemplate}
      />
    </Paper>
  );
}